import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaFilter, FaGlobe, FaIndustry, FaUsers, FaTimes, FaGraduationCap, FaBuilding } from 'react-icons/fa';
import predefinedUsers from '../components/auth/usersdata.json';
import { Link } from 'react-router-dom';

const InstituteDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filteredInstitutes, setFilteredInstitutes] = useState([]);
  const [filters, setFilters] = useState({
    industry: 'All Industries'
  });
  const [selectedOrg, setSelectedOrg] = useState(null);

  useEffect(() => {
    const storedInstitutes = JSON.parse(localStorage.getItem('nstituteDirectory') || '[]');
    const predefinedInstitutes = predefinedUsers.users.filter(user => 
      user.userType === 'institute' || user.userType === 'employer'
    );
    const allInstitutes = [...storedInstitutes, ...predefinedInstitutes];
    setFilteredInstitutes(allInstitutes);
  }, []);

  useEffect(() => {
    filterInstitutes();
  }, [searchTerm, filters]);

  const filterInstitutes = () => {
    const storedInstitutes = JSON.parse(localStorage.getItem('nstituteDirectory') || '[]');
    const predefinedInstitutes = predefinedUsers.users.filter(user => 
      user.userType === 'institute' || user.userType === 'employer'
    );
    const allInstitutes = [...storedInstitutes, ...predefinedInstitutes];

    let filtered = allInstitutes.filter(org => {
      const matchesSearch = org.instituteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (org.industry && org.industry.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesIndustry = filters.industry === 'All Industries' || org.industry === filters.industry;

      return matchesSearch && matchesIndustry;
    });

    setFilteredInstitutes(filtered);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const getInstituteAlumni = (orgId) => {
    const allAlumni = [...predefinedUsers.users.filter(user => user.userType === 'alumni'), 
                       ...JSON.parse(localStorage.getItem('alumniDirectory') || '[]')];
    return allAlumni.filter(alumni => alumni.instituteId === orgId);
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <header className="bg-blue-600 text-white py-6">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold">Institute Directory</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full md:w-1/2">
            <input
              type="text"
              placeholder="Search institutes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFilterOpen(!filterOpen)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <FaFilter className="mr-2" />
            Filter
          </motion.button>
        </div>

        {filterOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-8 p-4 bg-white rounded-lg shadow"
          >
            <h2 className="text-xl font-semibold mb-4">Filter Options</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700">Industry</label>
              <select
                name="industry"
                value={filters.industry}
                onChange={handleFilterChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option>All Industries</option>
                <option>Technology</option>
                <option>Finance</option>
                <option>Education</option>
                <option>Healthcare</option>
              </select>
            </div>
          </motion.div>
        )}

<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredInstitutes.map((org) => (
          <motion.div
            key={org.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <Link to={`/institute/${org.id}`} className="block p-4">
              <div className="flex items-center justify-center mb-4">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center"
                >
                  <FaBuilding className="text-4xl text-gray-600" />
                </motion.div>
              </div>
              <h2 className="text-xl font-semibold text-center mb-2">{org.instituteName}</h2>
              <div className="flex justify-center items-center space-x-2 mb-2">
                <FaIndustry className="text-blue-500" />
                <span>{org.industry}</span>
              </div>
              <p className="text-gray-600 text-center mb-4">{org.description}</p>
              <div className="flex justify-center space-x-4">
                {org.website && (
                  <FaGlobe className="text-blue-600" />
                )}
                <FaUsers className="text-green-500" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
      </main>

      <AnimatePresence>
        {selectedOrg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Alumni</h2>
                <button onClick={() => setSelectedOrg(null)} className="text-gray-500 hover:text-gray-700">
                  <FaTimes size={24} />
                </button>
              </div>
              <div className="space-y-4">
                {getInstituteAlumni(selectedOrg).map((alumni) => (
                  <div key={alumni.id} className="bg-gray-100 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold">{alumni.fullName}</h3>
                    <p className="text-gray-600">{alumni.major} - Class of {alumni.graduationYear}</p>
                    <div className="flex items-center mt-2">
                      <FaGraduationCap className="text-blue-500 mr-2" />
                      <span>{alumni.company || "Not specified"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstituteDirectory;